export const INCOME_TYPES = {
  W2: "w2",
  SELF_EMPLOYED: "self-employed",
  BOTH: "both",
};

export const RECOMMENDATION_RATES = {
  safe: 0.15,
  balanced: 0.25,
  aggressive: 0.4,
};

export const DEBT_METHODS = [
  { id: "snowball", label: "Snowball" },
  { id: "avalanche", label: "Avalanche" },
  { id: "highest-payment", label: "Highest Payment First" },
];

export function parseNumber(value) {
  const number = parseFloat(value);
  return Number.isNaN(number) ? 0 : number;
}

export function toMonthly(amount, frequency) {
  switch (frequency) {
    case "Weekly":
      return (amount * 52) / 12;
    case "Bi-weekly":
      return (amount * 26) / 12;
    case "Monthly":
      return amount;
    case "One-time":
      return amount / 12;
    default:
      return amount;
  }
}

export function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(!value || Number.isNaN(value) ? 0 : value);
}

export function safePercent(numerator, denominator) {
  if (!denominator || denominator <= 0) {
    return 0;
  }

  return (numerator / denominator) * 100;
}

export function formatMonths(months) {
  if (months === null || months === undefined) {
    return "Not available yet";
  }

  if (months <= 0) {
    return "0 months";
  }

  if (months < 12) {
    return `${Math.ceil(months)} months`;
  }

  return `${Math.ceil(months)} months (${(months / 12).toFixed(1)} years)`;
}

export function estimateDebtPayoff(balance, monthlyPayment, apr, extraPayment = 0) {
  const cleanedBalance = parseNumber(balance);
  const cleanedPayment = parseNumber(monthlyPayment) + parseNumber(extraPayment);
  const cleanedApr = parseNumber(apr);

  if (cleanedBalance <= 0) {
    return { months: 0, years: 0, totalPaid: 0, interestPaid: 0, status: "paid", message: "This debt is already paid off." };
  }

  if (cleanedPayment <= 0) {
    return { months: null, years: null, totalPaid: null, interestPaid: null, status: "no-payment", message: "Add a monthly payment to estimate payoff time." };
  }

  const monthlyRate = cleanedApr / 100 / 12;
  if (monthlyRate > 0 && cleanedPayment <= cleanedBalance * monthlyRate) {
    return { months: null, years: null, totalPaid: null, interestPaid: null, status: "negative-amortization", message: "This payment may be too low to pay off this debt." };
  }

  let months = 0;
  let totalPaid = 0;
  let remainingBalance = cleanedBalance;

  while (remainingBalance > 0 && months < 1200) {
    const interest = remainingBalance * monthlyRate;
    const totalDue = remainingBalance + interest;
    const payment = Math.min(cleanedPayment, totalDue);
    const principalPaid = payment - interest;

    if (principalPaid <= 0) {
      return { months: null, years: null, totalPaid: null, interestPaid: null, status: "negative-amortization", message: "This debt may negatively amortize or take extremely long to pay off." };
    }

    remainingBalance = Math.max(0, totalDue - payment);
    totalPaid += payment;
    months += 1;
  }

  if (months >= 1200) {
    return { months: null, years: null, totalPaid: null, interestPaid: null, status: "very-long", message: "This debt takes an extremely long time to pay off at the current payment." };
  }

  return { months, years: months / 12, totalPaid, interestPaid: Math.max(0, totalPaid - cleanedBalance), status: "ok", message: null };
}

export function buildGoalTimeline(monthlyContribution, targetAmount, kind) {
  let runningValue = 0;
  const cappedTarget = Math.max(0, targetAmount);

  return Array.from({ length: 12 }).map((_, index) => {
    runningValue = Math.min(cappedTarget, runningValue + Math.max(0, monthlyContribution));
    return {
      name: `M${index + 1}`,
      value: kind === "debt" ? Math.max(0, cappedTarget - runningValue) : runningValue,
    };
  });
}

export function getDebtMethodReason(method, debt) {
  if (method === "snowball") {
    return `${debt.name} is first because it has the smallest balance.`;
  }

  if (method === "avalanche") {
    return `${debt.name} is first because it has the highest APR.`;
  }

  return `${debt.name} is first because it has the largest monthly payment burden.`;
}
